// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Custom Key Signature
//
// For creating or editing a custom key signature.
// A custom key signature must be added to a music staff object -- not
// to music itself.  (This allows handling of different key signatures
// on different staves, if desired.)
import { keySignatureClefNotes } from 'definitions/musicTheory/keySignatures';
import type { ClefName } from 'definitions/musicTheory/clefs';
import type { Accidental, NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { StaveModifier, Glyph } from 'vexflow';
import type { Stave as VexStave } from 'vexflow';
import Note from './note';
import NoteHolder from './noteHolder';
import type { EditableOptions } from './editable';
import Editable from './editable';
import KeySignature from './keySignature';
import Clef from './clef';
import config from '../config';
import type Editor from './editor';

/** See VexFlow tables.ts:190 */
const accidentalCodes = {
  '#': { code: 'accidentalSharp', parenRightPaddingAdjustment: -1 },
  '##': { code: 'accidentalDoubleSharp', parenRightPaddingAdjustment: -1 },
  b: { code: 'accidentalFlat', parenRightPaddingAdjustment: -2 },
  bb: { code: 'accidentalDoubleFlat', parenRightPaddingAdjustment: -2 },
  n: { code: 'accidentalNatural', parenRightPaddingAdjustment: -1 },
};

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface BoxWithGlyph extends Box {
  glyphX: number;
  glyphW: number;
  glyph?: Glyph;
}

type NoteHolderWithEditable = NoteHolder & { editable: Editable };

export default class CustomKeySignature extends StaveModifier {
  accidentalHolders: NoteHolderWithEditable[] = [];

  accidental: 'b' | '#';

  lastIndex?: number;

  editableOptions: Partial<EditableOptions> = {
    rhythm: false,
    accidental: true,
    pitch: true,
    deleteable: ['ghost'],
    selected: false,
    selectable: true,
    fixedNotes: [],
    // ghosting: false,
    highestLine: 5.5,
    lowestLine: 0.5,
  };

  locked = false;

  /** uTheory Clef - must be initialized by setClef() before drawing */
  clef?: Clef;

  /** Initialized when format() is called */
  private editables?: Editable[];

  /** Initialized when format() is called */
  private glyphsHolders?: BoxWithGlyph[];

  /** amount by which to offset the whole key sig. (This is a VexFlow prop) */
  protected padding = 3;

  editor: Editor;

  // Public constructor
  constructor({ editor, accidental = 'b' }: { accidental?: '#' | 'b'; editor: Editor }) {
    super();
    this.editor = editor;
    this.setPosition(StaveModifier.Position.BEGIN);
    this.accidentalHolders = [];
    this.accidental = typeof accidental === 'undefined' ? 'b' : accidental;
    this.initializeHolders();
    this.setAccidentalType(this.accidental);
  }

  /**
   * Create seven noteHolders with Editables to hold up to
   * seven accidentals for this custom key signature.
   */
  private initializeHolders() {
    this.accidentalHolders = [];
    this.editableOptions = {
      rhythm: false,
      accidental: true,
      pitch: true,
      deleteable: ['ghost'],
      selected: false,
      selectable: true,
      fixedNotes: [],
      // ghosting: false,
      highestLine: 5.5,
      lowestLine: 0.5,
    };
    for (let i = 0; i < 7; i++) {
      this.addAccidental(this.editableOptions);
    }
  }

  /**
   * Create a note holder with editable for a key signature accidental.
   */
  private addAccidental(options: Partial<EditableOptions>, note?: Note | NoteName | '') {
    // Create the noteHolder:
    if (typeof note === 'undefined') note = '';
    const holder = new NoteHolder(note, 'w');

    // Create & add its editable object:
    holder.editable = new Editable(holder, options);

    // This will be set again in Format, at which point we'll throw an error if needed
    if (this.clef) {
      holder.editable.setClef(this.clef);
    }
    holder.editable.eventHandler.addListener(
      'select',
      () => {
        delete holder.color;
        delete holder.notes[0]!.additionalAriaText;
      },
      this
    );
    holder.editable.eventHandler.setAccidental(this.accidental);
    holder.editable.eventHandler.lockAccidental(this.accidental);
    // add it to accidentalHolders:
    this.accidentalHolders.push(holder as NoteHolderWithEditable);
  }

  /**
   * Change the type of all existing & future accidentals to #
   * or b.
   */
  setAccidentalType(accidental: '#' | 'b') {
    this.accidentalHolders.forEach((holder) => {
      holder.editable.eventHandler.lockAccidental(accidental);
      holder.editable.eventHandler.setAccidental(accidental);
      if (holder.editable.pendingNote) {
        holder.editable.pendingNote.accidental = accidental;
      }
    });
    this.accidental = accidental;
    if (this.getKeyNotes().length) this.editor.renderer.render();
  }

  get relativeMajor(): CustomKeySignature {
    return this;
  }

  /**
   * Return the accidental that should be applied to a note based on this
   * custom key signature. So, if our custom key is F#-Bb, then:
   * ```typescript
   *   // Custom key === F#-G#
   *   customKey.getAccidentalAtNote(new Note('F4')) === '#'
   * ```
   */
  public getAccidentalAtNote(note: Note): Accidental {
    const keyNotes = this.accidentalHolders
      .slice(0)
      .reverse()
      .map((holder) => holder.notes[0] || (holder.editable && holder.editable.pendingNote))
      .filter((n) => n);

    const exactMatch = keyNotes.find((keyNote) => keyNote && keyNote.equalsIgnoreAccidental(note));
    if (exactMatch) {
      return exactMatch.accidental;
    }

    const octaveMatch = keyNotes.find((keyNote) => keyNote && keyNote.letter === note.letter);
    if (octaveMatch) {
      return octaveMatch.accidental;
    }
    return '';
  }

  /**
   * Does the note exist in this key signature or not?
   */
  public contains(note: Note) {
    const withAccidental = this.applyKeySignatureToNote(note.clone());
    return withAccidental.equals(note);
  }

  /**
   * Change the accidental of the note to match the key signature. Caution: Modifies
   * the note passed as an argument directly, DOES NOT clone it.
   */
  public applyKeySignatureToNote(note: Note | NoteName): Note {
    note = note instanceof Note ? note : Note.stringToNote(note);
    const accidental = this.getAccidentalAtNote(note);
    note.accidental = accidental;
    return note;
  }

  /**
   * Delete the selected accidental, if none is seleted, delete the
   * accidental farthest to the right.
   */
  public delete() {
    if (this.locked) return;
    const holderToDelete = this.selectIfNeeded();
    if (!holderToDelete) return;
    const index = this.accidentalHolders.indexOf(holderToDelete);
    this.accidentalHolders.splice(index, 1);
    if (this.accidentalHolders.length < 7) {
      this.addAccidental(this.editableOptions);
    }
    this.lastIndex = index;
    this.selectLastSelected();
    this.editor.renderer.render();
  }

  /**
   * Empty the key signature of all accidentals.
   */
  public clear() {
    this.initializeHolders();
    this.editor.renderer.render();
  }

  /**
   * Selects the latest accidental, if no other accidental's been selected
   */
  private selectIfNeeded() {
    const selected = this.accidentalHolders.find((h) => h.editable.selected);
    if (selected) return selected;
    const lastUnemptyHolder = this.accidentalHolders
      .slice(0)
      .reverse()
      .find((holder) => !!holder.length);
    if (lastUnemptyHolder) {
      lastUnemptyHolder.editable.selected = true;
      delete lastUnemptyHolder.editable.noteHolder.color;
      delete lastUnemptyHolder.editable.noteHolder.notes[0]!.additionalAriaText;
      this.editor.renderer.redraw();
      return lastUnemptyHolder;
    }
    return false; // nothing to select
  }

  /**
   * Select the previously selected accidental, if one exists there. If one does not,
   * but one does at the index before that, select that instead.
   */
  private selectLastSelected() {
    if (typeof this.lastIndex === 'undefined') return;
    if (
      !this.accidentalHolders[this.lastIndex]?.isEmpty() &&
      !this.accidentalHolders[this.lastIndex]?.editable.pendingNote
    ) {
      this.accidentalHolders[this.lastIndex]!.editable.select();
      this.accidentalHolders[this.lastIndex]!.editable.redraw();
    } else if (
      this.lastIndex > 0 &&
      // There is a holder at one minus the last index
      this.accidentalHolders[this.lastIndex - 1] &&
      // and it's not empty...
      !this.accidentalHolders[this.lastIndex - 1]!.isEmpty()
    ) {
      this.accidentalHolders[this.lastIndex - 1]!.editable.select();
      this.accidentalHolders[this.lastIndex - 1]!.editable.redraw();
    }
  }

  /**
   * Selects the next accidental to the left of currently selected accidental
   */
  public selectLeft() {
    if (this.locked) return;
    for (let i = 1; i < this.accidentalHolders.length; i++) {
      const holder = this.accidentalHolders[i]!;
      const priorHolder = this.accidentalHolders[i - 1]!;
      if (holder.editable.selected) {
        holder.editable.selected = false;
        priorHolder.editable.selected = true;
        delete priorHolder.color;
        if (priorHolder.notes[0]) {
          delete priorHolder.notes[0]!.additionalAriaText;
        }
        // Only redraw is needed because just colors changed.
        this.editor.renderer.redraw();
        return;
      }
    }
    this.selectIfNeeded();
  }

  /**
   * selects the next accidental to the right of currently selected
   */
  public selectRight() {
    if (this.locked) return;
    for (let i = 0; i < this.accidentalHolders.length - 1; i++) {
      const holder = this.accidentalHolders[i]!;
      if (i === 0) {
        // If it's the first holder, and it has a pending note, turn the
        // pending note into an actual note.
        if (holder.editable.pendingNote) {
          holder.editable.pendingNote = false;
          holder.editable.selected = true;
          delete holder.color;
          if (holder.notes[0]) {
            delete holder.notes[0]!.additionalAriaText;
          }
          // Only redraw is needed because just colors changed.
          this.editor.renderer.redraw();
          return;
        }
        // Else, if it's the first holder and it's empty, add a note on the third line.
        if (holder.isEmpty()) {
          if (!holder.editable.clef) {
            throw Error('customKeySignature.setClef must be called before selecting right.');
          }
          const note = holder.editable.clef.getNoteAtLine(3);
          holder.notes[0] = note;
          note.accidental = this.accidental;
          holder.editable.pendingNote = false;
          holder.editable.selected = true;
          delete holder.color;
          delete holder.editable.noteHolder.notes[0]!.additionalAriaText;
          this.editor.renderer.render();
          return;
        }
      }

      // If it's not the first accidental and it's selected
      if (holder.editable.selected) {
        const nextHolder = this.accidentalHolders[i + 1]!;
        // ...if the next one is empty, add a note on the same line as this note
        if (nextHolder.isEmpty()) {
          nextHolder.notes[0] = holder.notes[0]!.clone();
          holder.editable.selected = false;
          nextHolder.editable.selected = true;
          this.editor.renderer.render();
          return;
        }

        // Otherwise, if it has a pending note, commit it:
        if (nextHolder.editable.pendingNote) {
          holder.editable.selected = false;
          nextHolder.editable.pendingNote = false;
          nextHolder.editable.selected = true;
          this.editor.renderer.redraw();
          return;
        }

        // Otherwise, this and the next exist, so just change who's selected:
        holder.editable.selected = false;
        nextHolder.editable.selected = true;
        this.editor.renderer.redraw();
        return;
      }
    }
    this.selectIfNeeded();
  }

  /**
   * Moves the currently selected accidental up. If no visible accidentals,
   * adds one instead.
   */
  public moveUp() {
    if (this.locked) return;
    // is there nothing on screen? Add a note
    if (!this.accidentalHolders.find((holder) => holder.notes.length)) {
      this.selectRight();
      return;
    }

    this.accidentalHolders.forEach((holder) => {
      if (holder.editable.selected) {
        const editable = holder.editable;
        if (!editable.clef) {
          throw Error('customKeySignature.setClef must be called before moving up.');
        }

        const note = holder.notes[0]!;
        const line = editable.clef.getLine(note);
        if (line < editable.options.highestLine) {
          holder.notes[0] = editable.clef.getNoteAtLine(line + 0.5);
          holder.notes[0].accidental = this.accidental;
          this.editor.renderer.redraw();
        }
      }
    });
  }

  /**
   * Moves the currently selected accidental up. If no visible accidentals,
   * adds one instead.
   */
  public moveDown() {
    if (this.locked) return;
    // is there nothing on screen? Add a note
    if (!this.accidentalHolders.find((holder) => holder.notes.length)) {
      this.selectRight();
      return;
    }

    this.accidentalHolders.forEach((holder) => {
      if (holder.editable.selected) {
        const editable = holder.editable;
        if (!editable.clef) {
          throw Error('customKeySignature.setClef must be called before selecting right.');
        }
        const note = holder.notes[0]!;
        const line = editable.clef.getLine(note);
        if (line > editable.options.lowestLine) {
          holder.notes[0] = editable.clef.getNoteAtLine(line - 0.5);
          holder.notes[0].accidental = this.accidental;
          this.editor.renderer.redraw();
        }
      }
    });
  }

  /**
   * Prevent alterations to the key signature, disallow all editing.
   */
  public lockSignature() {
    this.locked = true;
    this.accidentalHolders.forEach((holder) => {
      holder.editable.options.pitch = false;
      holder.editable.options.selectable = false;
      holder.editable.selected = false;
      if (holder.editable.pendingNote) {
        holder.remove(0);
        holder.editable.pendingNote = false;
      }
    }, this);
    this.editor.renderer.render();
  }

  /**
   * Enable/allow editing of the custom key signature.
   */
  unlockSignature() {
    this.locked = false;
    this.accidentalHolders.forEach((holder) => {
      holder.editable.options.pitch = true;
      holder.editable.options.selectable = true;
      holder.editable.selected = false;
      if (holder.editable.pendingNote) {
        holder.remove(0);
        holder.editable.pendingNote = false;
      }
    }, this);
    this.editor.renderer.render();
  }

  /**
   * Get all of the notes drawn in this custom key signature. (Which may or may
   * not form a valid key.)
   */
  public getKeyNotes(): Note[] {
    const keyNotes: Note[] = [];
    this.accidentalHolders.forEach((holder) => {
      if (holder.length) {
        keyNotes.push(holder.notes[0]!.clone());
      }
    });
    return keyNotes;
  }

  /**
   * Get what would have been the correct accidental note names for the number
   * of notes drawn if this were (or is) a valid key signature.
   * @param clef
   */
  private getCorrectNoteNameArray(clef: ClefName | Clef): NoteName[] {
    const keyNotes = this.getKeyNotes();

    if (!keyNotes.length) return [];

    // Be sure we have a clef:
    if (typeof clef === 'undefined')
      throw new Error('NaC: Clef must be provided to check key signature validity.');

    const clefName = new Clef(clef).getName();
    const accidental = keyNotes[0]!.accidental === 'b' ? 'flats' : 'sharps';
    const correctNotesArray = keySignatureClefNotes[accidental][clefName];
    return correctNotesArray;
  }

  /**
   * Checks whether the currently drawn key signature is a
   * valid key signature. If valid, returns KeySignature,
   * if invalid returns an array of all the notes that are wrong
   * as Note[]
   */
  checkKey(clef?: Clef | ClefName): KeySignature | Note[] {
    clef = clef || this.clef!;
    if (!clef) {
      throw Error(
        'A clef must be provided to customKeySignature.checkKey(clef), or set via customKeySignature.setClef()'
      );
    }
    /** All of the notes drawn so far */
    const keyNotes = this.getKeyNotes();

    // C Major or A Minor:
    if (keyNotes.length === 0) return new KeySignature('C');

    const correctNoteArray = this.getCorrectNoteNameArray(clef);

    // Be sure we have a clef:
    if (typeof clef === 'undefined')
      throw new Error('NaC: Clef must be provided to check key signature validity.');

    const accidental = this.accidental === 'b' ? 'flat' : 'sharp';

    // Compare the notes:
    const wrongNotes = keyNotes.filter((note, index) => {
      const correctNote = correctNoteArray[index];
      return correctNote && !note.equals(correctNote);
    });

    // If it's an INVALID key signature -- here are the notes that don't belong:
    if (wrongNotes.length !== 0) return wrongNotes;

    // Otherwise, here is the key that was drawn:
    if (accidental === 'flat') return new KeySignature(-keyNotes.length);
    return new KeySignature(keyNotes.length);
  }

  /**
   * Color incorrect accidentals based on the correct key & clef. Returns
   * string hint text if needed, in HTML/accidentalFont if requested.
   */
  hint(correctKey: KeySignature, clef?: Clef | ClefName, accidentalFont?: boolean) {
    clef = clef || this.clef!;
    if (!clef) {
      throw Error(
        'A clef must be provided to customKeySignature.checkKey(clef), or set via customKeySignature.setClef()'
      );
    }
    clef = new Clef(clef);

    // Deselect everything:
    this.accidentalHolders.forEach((holder) => {
      holder.editable.selected = false;
    });

    // Did they draw the parallel key instead?
    const drawnKey = this.checkKey();
    if (
      // They have drawn a valid key signature:
      drawnKey instanceof KeySignature
    ) {
      try {
        const parallelKey = correctKey.getParallelKey();
        if (parallelKey.accidentalsCount === drawnKey.accidentalsCount) {
          return `You've written ${correctKey.keyNote.toAccidentalFontShortString()} ${
            parallelKey.quality
          } instead of ${correctKey.toAccidentalFontString()}.`;
        }
      } catch (err) {
        // Swallow error.
      }
    }

    // Start with the obvious: are they doing a sharp/flat key right?
    // __ is a sharp key
    if (
      correctKey.accidentalsCount > 0 &&
      this.accidental === 'b' &&
      this.accidentalHolders[0] &&
      !this.accidentalHolders[0].isEmpty()
    ) {
      return accidentalFont
        ? `${correctKey.toAccidentalFontString()} is a sharp key.`
        : `${correctKey.toString()} is a sharp key.`;
    }

    // __ is a flat key
    if (
      correctKey.accidentalsCount < 0 &&
      this.accidental === '#' &&
      this.accidentalHolders[0] &&
      !this.accidentalHolders[0].isEmpty()
    ) {
      return accidentalFont
        ? `${correctKey.toAccidentalFontString()} is a flat key.`
        : `${correctKey.toString()} is a flat key.`;
    }

    // Get wrong compared to desired key sig:
    const thisNotes = this.getKeyNotes();
    const rightNotes = correctKey.getKeyNotesForClef(clef);

    // correctKey.getKeyNotes().map(Note.fromStringOrNote);

    // Get indexes for any sharps/flats that are in the wrong order:
    const keyCheck = this.checkKey(clef);
    let wrongOrderIndexesArray: number[] = [];
    if (!(keyCheck instanceof KeySignature)) {
      wrongOrderIndexesArray = keyCheck
        .map((note) => thisNotes.findIndex((answerNote) => answerNote === note))
        .filter((index) => index !== -1);
    }

    // Get index for any sharps/flats that don't belong in the key:
    const wrongNotesArray: number[] = [];
    for (let i = 0; i < thisNotes.length && i < rightNotes.length; i++) {
      const holder = this.accidentalHolders[i];
      const thisNote = thisNotes[i];
      const rightNote = rightNotes[i];
      // If there's not a right note, we have too many accidentals, we'll give that clue
      // so we can skip coloring it.
      if (holder && !holder.isEmpty() && thisNote && rightNote && !thisNote.equals(rightNote)) {
        wrongNotesArray.push(i);
      }
    }

    // Mark existing wrong accidentals:
    const wrongArray = wrongNotesArray.concat(wrongOrderIndexesArray);
    wrongArray.forEach((index) => {
      const holder = this.accidentalHolders[index];
      if (holder) {
        holder.editable.selected = false;
        holder.color = 'red';
        if (holder.notes[0]) {
          holder.notes[0].additionalAriaText = ' is wrong';
        }
      }
    });

    this.editor.renderer.redraw();

    // Send back hint text:
    // Too many accidentals
    if (thisNotes.length < rightNotes.length) {
      return 'Too few accidentals.';
    }
    // Too few accidentals
    if (thisNotes.length > rightNotes.length) {
      return 'Too many accidentals.';
    }

    // Right accidentals, wrong order:
    const rightLength = thisNotes.length === rightNotes.length;
    const thisNotesAreAllPresentInRightNotes = thisNotes.every((thisNote) =>
      rightNotes.find((rightNote) => rightNote.equals(thisNote))
    );
    const rightNotesAreAllPresentInThisNotes = rightNotes.every(
      (rightNote) => !!thisNotes.find((thisNote) => thisNote.equals(rightNote))
    );
    const rightOrder = thisNotes.every(
      (thisNote, index) => rightNotes[index] && thisNote.equals(rightNotes[index]!)
    );

    if (
      rightLength &&
      thisNotesAreAllPresentInRightNotes &&
      rightNotesAreAllPresentInThisNotes &&
      !rightOrder
    ) {
      return 'Right accidentals, wrong order.';
    }

    return undefined;
  }

  /**
   * Assign the VexFlow stave
   */
  setStave(vexStaff: VexStave) {
    this.stave = vexStaff;
    // this.stave.addModifier(this, Vex.Flow.StaveModifier.Position.LEFT);
    return this;
  }

  /**
   * Assign the uTheory Clef
   */
  setClef(clef: Clef) {
    this.clef = clef;
    return this;
  }

  /**
   * Width in SVG pixels. Used for rendering to know where to start the
   * following objects.
   */
  // @ts-ignore – because finding everywhere that customKeySignature.width is called
  // within VexFlow would be prohibitively difficult, and I don't want to introduce
  // reactive logic to force a recalculation of this each time an accidental changes
  // in the custom key signature.
  get width() {
    const width =
      this.accidentalHolders
        .filter(
          (noteHolder) =>
            noteHolder.length || (noteHolder.editable && noteHolder.editable.pendingNote)
        )
        .reduce((totalWidth, noteHolder) => {
          const note =
            noteHolder.notes[0] || (noteHolder.editable && noteHolder.editable.pendingNote);
          if (!note) return totalWidth; // this should never happen;

          // eslint-disable-next-line @typescript-eslint/dot-notation
          const glyphCache = Glyph['cache'].lookup(
            accidentalCodes[note.toVexAccidental()].code,
            'accidental'
          );
          if (!glyphCache) {
            throw Error(`Lookup of glyph metrics for accidental.${note.toVexAccidental()} failed.`);
          }

          // Font width to vex width ratio seems to be about 35... I don't know why, but it works.
          const glyphWidthToSVGSpaceWidth = 36;
          const glyphWidth = glyphCache.bbox.w / glyphWidthToSVGSpaceWidth;
          // const glyphData = Vex.Flow.Tables.accidentalCodes(note.toVexAccidental());
          return totalWidth + glyphWidth;
        }, 0) + this.padding;
    return Math.max(width, 0);
  }

  // eslint-disable-next-line
  set width(n: number) { }

  getEditables() {
    return this.editables || [];
  }

  /**
   * Vex format routine
   */
  public format() {
    const vexStaff = this.stave;
    if (!vexStaff) {
      throw Error(
        'Must call customKeySignature.setStave(VexStaff) before calling customKeySignature.format()'
      );
    }
    const context = vexStaff.getContext();
    this.setContext(context);
    const uTheoryClef = this.clef;
    if (!uTheoryClef) {
      throw Error(
        'customKeySignature.setClef() must be called before attempting to format or draw.'
      );
    }

    const editables: Editable[] = []; // a list of this key sig's editables...
    // For each note in the key signature:
    const glyphsHolders: BoxWithGlyph[] = [];

    if (typeof this.x === 'undefined') {
      throw Error('customKeySignature.setX() must be called before customKeySignature.format()');
    }
    let x: number = this.x;

    let lastHolderHasBeenFormatted = false;
    this.accidentalHolders.forEach((holder, i) => {
      if (lastHolderHasBeenFormatted) return;
      let line: number;
      let box: BoxWithGlyph;
      const last = holder.isEmpty() || !!holder.editable.pendingNote;

      // Note: Editable may have been deleted if we're locked.
      const isPendingNote = holder.editable?.pendingNote;

      // if it's empty & ghosting, just set it's bounding box & be done:
      if (holder.isEmpty() && !isPendingNote) {
        box = {
          x,
          y: vexStaff.getYForLine(-1),
          w: 30, // this will be overridden anyway...
          h: vexStaff.getYForLine(5.5) - vexStaff.getYForLine(-1),
          glyphX: x,
          glyphW: 30,
        };
        // pad if it's the first one:
        //          if(i===0) box.x = 5;
        //            if(i===0) box.x = vexStaff.x;

        // Otherwise: let's display it!
      } else {
        const note = holder.notes[0] || (holder.editable.pendingNote as Note);
        // if(holder.isEmpty()) line = null;
        line = uTheoryClef.getVexLine(note);

        const acc = {
          type: note.toVexAccidental(),
          line,
        };
        // If it's ghosting, it's the last one we display...
        const first = i === 0;
        let color = holder.color;

        if (holder.editable.pendingNote) color = config.Colors.editorColors.ghosting;
        if (holder.editable.selected) color = config.Colors.editorColors.selected;
        box = this.addAccToStave(vexStaff, acc, first, last, color, x);
        x += box.glyphW;
      }
      // Setup the editable:
      if (!this.locked) {
        holder.editable.setClef(uTheoryClef);
        holder.editable.setVexStave(vexStaff);
        holder.editable.setBoundingBox(box);
        editables.push(holder.editable);
      }
      glyphsHolders.push(box);
      if (last) lastHolderHasBeenFormatted = true;
    });

    this.glyphsHolders = glyphsHolders;
    this.editables = editables;
    return this;
    //      this.customKeySignaturesEditables.push(editables);
  }

  /**
   * Vex draw routine
   */
  draw() {
    // tuck up closer to the key:
    // this.x += this.padding;
    this.format();
    if (typeof this.x !== 'number') {
      throw Error('customKeySignature.setX() must be called before customKeySignature.draw()');
    }
    let x: number = this.x;

    const vexStaff = this.stave;
    if (!vexStaff) {
      throw Error('customKeySignature.setStave() must be called before customKeySignature.draw()');
    }

    // this.glyphsHolders is set in this.format
    this.glyphsHolders!.forEach((holder) => {
      if (!holder.glyph) return;
      holder.glyph.setStave(vexStaff);
      holder.glyph.setContext(vexStaff.getContext());
      holder.glyph.renderToStave(x);
      x += holder.glyph.getMetrics().width;
    });
  }

  private addAccToStave(
    stave: VexStave,
    acc: { type: 'b' | '#' | 'bb' | '##' | 'n'; line: number },
    first: boolean,
    last: boolean,
    color: string | undefined,
    x: number
  ): BoxWithGlyph {
    const context = this.getContext();
    if (!context) {
      throw new Error('Must have context to draw custom key signature.');
    }
    // acc should have type : "b" "bb" "##" "#" "n"
    //            and  line : 0 (top line) 4 (bottom line)

    /** See VexFlow keysignature.ts:98 -- this is a fixed value */
    const glyphFontScale = 38; // default font scale for key sigs in Vex.Flow
    const accGlyphData = accidentalCodes[acc.type];
    const glyph = new Glyph(accGlyphData.code, glyphFontScale);

    // Set the style for the glyph:
    if (typeof color !== 'undefined') {
      glyph.setStyle({ fillStyle: color, strokeStyle: color });
    }

    const yShift = stave.getYForLine(acc.line) - stave.getYForGlyphs();
    glyph.setYShift(yShift);

    // Set the bounding box:
    const glyphHeight = glyph.getMetrics().height * 2;
    const top = stave.getYForLine(-1);
    const glyphTop = stave.getYForGlyphs() + yShift - glyphHeight;
    const bottom = stave.getYForLine(5.5);
    const glyphBottom = glyphTop + 2 * glyphHeight;
    const w = glyph.bbox.getW();
    // if (last) w += 30;
    const h = Math.max(bottom, glyphBottom) - top;

    const box = {
      x,
      y: Math.min(top, glyphTop),
      w: glyph.bbox.getW(),
      h,
      glyphX: x,
      glyphW: w,
      glyph,
    };

    // If it's the first one, include the area of the clef as touchable:
    if (first) {
      box.w += box.x - stave.getX();
      box.x = stave.getX();
    }

    return box;
  }

  /**
   * Return a screen-reader friendly description of the custom key signature
   */
  toAriaString() {
    if (!this.editables) {
      throw Error(
        'customKeySignature.toAriaString() cannot be called before customKeySignature.format()'
      );
    }
    const accidentals = this.editables
      .map((editable) => {
        const noteString = editable.noteHolder.notes
          .map((note) => note.toAriaString({ rhythm: false }))
          .join(', ');
        const selectedString = noteString && editable.selected ? ' is selected' : '';
        return noteString + selectedString;
      })
      .filter((n) => n);

    if (accidentals.length) return accidentals.join('-');
    return 'no sharps or flats';
  }

  /**
   * Return a string representation of the custom key signature
   */
  toString() {
    if (!this.editables) {
      throw Error(
        'customKeySignature.toAriaString() cannot be called before customKeySignature.format()'
      );
    }
    return this.editables
      .map((editable) => editable.noteHolder.notes.map((note) => note.toString()).join(''))
      .filter((n) => n)
      .join(' ');
  }
}
